import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { IndianRupee, Printer, Trash2 } from 'lucide-react';
import { printLabBill, rupee } from './labPrint';
import { ModeToggle } from './LabBillForm';
import { errMsg } from './labApi';

const CollectBox = ({ bill, api, onDone }) => {
  const [amt, setAmt] = useState(String(bill.balance));
  const [mode, setMode] = useState('cash');
  const collect = async () => {
    try {
      const b = await api.post(`/bills/${bill.id}/collect`, { amount: parseFloat(amt) || 0, mode });
      toast.success(`Collected ${rupee(parseFloat(amt))} for ${b.bill_no}`);
      onDone(b);
    } catch (e) {
      toast.error(errMsg(e, 'Could not collect'));
    }
  };
  return (
    <div className="flex items-center gap-2 mt-2 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid={`lab-collect-box-${bill.id}`}>
      <ModeToggle value={mode} onChange={setMode} testId={`lab-collect-mode-${bill.id}`} />
      <Input inputMode="decimal" value={amt} onChange={(e) => setAmt(e.target.value.replace(/[^0-9.]/g, ''))} className="h-8 w-28 text-right" data-testid={`lab-collect-amount-${bill.id}`} />
      <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={collect} data-testid={`lab-collect-confirm-${bill.id}`}>Collect</Button>
    </div>
  );
};

const BillRow = ({ bill, api, onChange, showDate }) => {
  const [open, setOpen] = useState(false);
  const remove = async () => {
    if (!window.confirm(`Delete bill ${bill.bill_no}? This cannot be undone.`)) return;
    try { await api.del(`/bills/${bill.id}`); toast.success('Bill deleted'); onChange(); } catch (e) { toast.error(errMsg(e, 'Delete failed')); }
  };
  return (
    <div className="p-3 border rounded-xl bg-white" data-testid={`lab-bill-${bill.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs text-slate-500">{bill.bill_no}</span>
            {showDate && <span className="text-xs text-slate-400">{bill.date}</span>}
            <span className="font-bold">{bill.patient_name}</span>
            <span className="text-xs text-slate-500">{bill.age} {bill.sex && `• ${bill.sex}`}</span>
          </div>
          <p className="text-xs text-slate-600 truncate">{bill.items.map((i) => i.name).join(', ')}{bill.referral_doctor && ` — Ref: ${bill.referral_doctor}`}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="font-black" data-testid={`lab-bill-total-${bill.id}`}>{rupee(bill.total)}</p>
          {bill.balance > 0
            ? <p className="text-xs font-bold text-red-600" data-testid={`lab-bill-balance-${bill.id}`}>Due {rupee(bill.balance)}</p>
            : <p className="text-xs font-bold text-emerald-600" data-testid={`lab-bill-paid-${bill.id}`}>Paid</p>}
        </div>
      </div>
      <div className="flex gap-1.5 mt-2">
        {bill.balance > 0 && (
          <Button size="sm" variant="outline" className="h-7 text-xs border-amber-400 text-amber-700" onClick={() => setOpen(!open)} data-testid={`lab-collect-${bill.id}`}>
            <IndianRupee className="h-3 w-3 mr-1" /> Collect Balance
          </Button>
        )}
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => printLabBill(bill)} data-testid={`lab-print-${bill.id}`}><Printer className="h-3 w-3 mr-1" /> Print</Button>
        <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500 ml-auto" onClick={remove} data-testid={`lab-delete-${bill.id}`}><Trash2 className="h-3 w-3" /></Button>
      </div>
      {open && <CollectBox bill={bill} api={api} onDone={(b) => { setOpen(false); onChange(); printLabBill(b); }} />}
    </div>
  );
};

export const LabBillsList = ({ bills, api, onChange, title, showDate, empty }) => (
  <Card className="p-5" data-testid="lab-bills-list">
    <h3 className="font-bold mb-3">{title} <span className="text-slate-400 font-normal">({bills.length})</span></h3>
    <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
      {bills.length === 0 && <p className="text-sm text-slate-400 text-center py-8">{empty}</p>}
      {bills.map((b) => <BillRow key={b.id} bill={b} api={api} onChange={onChange} showDate={showDate} />)}
    </div>
  </Card>
);
